import {
  Box,
  Button,
  Card,
  Image,
  Loader,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Notification,
  Page,
  WixDesignSystemProvider,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";
import React, { useEffect } from "react";
import { ReactTagManager } from "react-gtm-ts";
import Track from "./divbg.png";
import { useTranslation } from "react-i18next";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const SLIDE_COUNT = 4;
const INTERVAL = 4000;
const BASE_URL = `https://wix.certifiedcode.io/dividers/_functions`;

function App() {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);

  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  });

  const [index, setIndex] = React.useState<number>(0);
  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] =
    React.useState<boolean>(true);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    const sliderInterval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % SLIDE_COUNT);
    }, INTERVAL);

    return () => clearInterval(sliderInterval);
  }, []);

  useEffect(() => {
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    setInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          setInstanceData(data);
          const upgraded = data.instance?.isFree === false;
          setIsUpgraded(upgraded);
          setIsUpgradeBannerOpen(!upgraded);

          if (
            !data.instance.permissions.includes("WIX_DEVELOPERS.GET_SITE_OWNER")
          ) {
            window.open(
              `https://go.certifiedcode.global/get/${WIX_APP_ID}/${data.site.siteId}`,
              "_blank"
            );
          }
        }
      })
      .catch(() => {});
  }

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${WIX_APP_ID}&redirectUrl=https://wix.certifiedcode.io/dividers/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeRedirectionUrl = `https://memberships.certifiedcode.us/${window.location.search}`;

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <>
              <Notification
                theme="premium"
                show={isUpgradeBannerOpen}
                type={"sticky"}
              >
                <Notification.TextLabel>
                  {t("upgradeMessage", { content: t(`content.${index}`) })}
                </Notification.TextLabel>
                <Notification.ActionButton
                  type="textLink"
                  link={upgradeRedirectionUrl}
                  target="_blank"
                  onClick={() => {
                    track("upgrade_clicked", { source: "banner" });
                  }}
                >
                  {t("upgrade-button")}
                </Notification.ActionButton>
                <Notification.CloseButton
                  onClick={() => setIsUpgradeBannerOpen(false)}
                />
              </Notification>
              <Box height="840px" verticalAlign="middle">
                <MarketingPageLayoutContent
                  title={t("divider-title")}
                  content={t("divider-content")}
                  actions={
                    <Box direction="vertical" gap="SP2">
                      <Button
                        skin="premium"
                        prefixIcon={<Icons.PremiumFilled />}
                        as="a"
                        target="_blank"
                        href={upgradeRedirectionUrl}
                        onClick={() => track("upgrade_clicked", { source: "cta" })}
                      >
                        {isUpgraded ? t("manage-sub-button") : t("upgrade-app-button")}
                      </Button>
                      <Button
                        priority="secondary"
                        prefixIcon={<Icons.GetStarted />}
                        onClick={() => {
                          track("setup_guide_clicked");
                          window.Intercom("showArticle", 8678985);
                        }}
                      >
                        {t("setup_guide")}
                      </Button>
                      <Button
                        skin="inverted"
                        prefixIcon={<Icons.Reviews />}
                        as="a"
                        href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                        target="_blank"
                        onClick={() => track("write_review_clicked")}
                      >
                        {t("five-star-button")}
                      </Button>
                    </Box>
                  }
                />
              </Box>
            </>
          }
          image={<Image transparent borderRadius={0} src={Track} />}
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
